import React, { useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import useBundleTranslation from 'i18n';
import { FormRendererAPIType, RawFormComponentType } from 'components/common/form';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { prepareFormElementProps } from 'components/common/form/formTools';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { instance } from 'api/api';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { alpha } from '@mui/material';
import { AssocArray } from 'tools/types';
import AssociateConfirmationPopup from './associate-rule/AssociateConfirmationPopup';

interface IProps {
    handleHidePopup: () => void;
    reloadGridData?: () => void;
}

export type AlertType = {
    name: string;
    viz: string;
};

export type ConfirmationData = {
    element: number;
    name: string;
    metrics: AssocArray<string>;
    alerts: AssocArray<{
        user: AlertType[];
        global: AlertType[];
    }>;
};

interface IConfirmationResponse {
    status: 'OK' | 'ERROR';
    data: ConfirmationData;
    message?: string;
}

export default function AssociateRule({ handleHidePopup, reloadGridData }: IProps) {
    const { t } = useBundleTranslation(['app/editor/element/visualization-for-alert-rule']);
    const { elementId } = useParams();

    const [showForm, setShowForm] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmationData, setConfirmationData] = useState<ConfirmationData>();

    const formPopupSettings: PopupSettings = {
        title: t('grid_visualization_for_alert_rule_add_popup_title'),
        textOK: t('popup_ok_button'),
    };

    const {
        setValue: hookFormSetValue,
        control,
        getValues: hookFromGetValues,
        watch,
    } = useForm({
        defaultValues: {
            metric_element_id: '[]',
        },
    });

    const formActions = {
        hookFormControl: control,
        hookFormGetValues: hookFromGetValues,
        hookFormSetValue: hookFormSetValue,
        hookFormWatch: watch,
        formDidMount: true,
    } as FormRendererAPIType;

    const checkBoxProps = {
        form: formActions,
        component: {
            component: 'CheckBoxList',
            name: 'metric_element_id',
            props: {
                async: true,
                settingsUrl: '/data/editor/element/:elementId/visualization-for-alert-rule/get',
                height: 400,
                showCounter: false,
                urlParams: {
                    elementId,
                },
            },
        } as RawFormComponentType,
    };

    const metricElementIds = JSON.parse(watch('metric_element_id'));

    const handleOnHide = () => {
        setShowForm(false);
        handleHidePopup();
    };

    const handleOnFormSubmit = () => {
        setLoading(true);
        instance
            .post<IConfirmationResponse>(`data/editor/element/${elementId}/visualization-for-alert-rule/confirm`, {
                metric_element_id: metricElementIds,
            })
            .then((response) => {
                if (response.data.status === 'OK') {
                    setShowConfirm(true);
                    setConfirmationData(response.data.data);
                }
                if (response.data.status === 'ERROR') {
                    alert(response.data.message);
                    handleHidePopup();
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleOnConfirm = () => {
        setLoading(true);

        instance
            .post(`data/editor/element/${elementId}/visualization-for-alert-rule/0/form`, {
                metric_element_id: metricElementIds,
            })
            .then((response) => {
                setShowConfirm(false);
                setLoading(false);
                if (reloadGridData) {
                    reloadGridData();
                }
                handleHidePopup();
            });
    };

    const handleOnConfirmHide = () => {
        setShowConfirm(false);
    };

    return (
        <>
            {showForm && (
                <Popup
                    settings={formPopupSettings}
                    maxWidth="popupMd"
                    onHide={handleOnHide}
                    open={showForm}
                    onConfirm={handleOnFormSubmit}
                    okDisabled={metricElementIds.length === 0}
                >
                    <ReactHookFormController
                        elementProps={prepareFormElementProps(checkBoxProps)}
                    ></ReactHookFormController>
                </Popup>
            )}
            {confirmationData && showConfirm && (
                <AssociateConfirmationPopup
                    onConfirm={handleOnConfirm}
                    onHide={handleOnConfirmHide}
                    showConfirm={showConfirm}
                    data={confirmationData}
                />
            )}
            {loading && <LoadingPlaceholder variant={'secondary'} relativeToParent />}
        </>
    );
}
